import React, { useState, useEffect, useRef, useCallback } from "react";
import { SlidersHorizontal, HelpCircle, Settings, ShoppingCart, House, Globe, ChevronRight } from "lucide-react";
import i18n from "../../../i18n";

const PreviewColumn = ({
    // Layout
    boxWidth, boxHeight, previewScale,
    // Geometry
    vWidth, vShort, vLong, tipH, taperOffsetTop, vBuckleWidth, strokeWidth, scale,
    fullW, lugOff, bucOff,
    previewShortLeft, previewShortTop, previewLongLeft, previewLongTop,
    previewClipPathShort, previewClipPathLong,
    // Texture
    imageUrl, trueWidth, imgDisplayHeight,
    shortPos, longPos, yScale, strapRotation,
    // State
    isOrderFinished, isConfirmed, isConfigOpen,
    // Handlers
    setIsConfigOpen, setActiveMobileView, onStartTour,
    // Ref
    previewContainerRef,
    // Locale
    localesList, currentLocale, setCurrentLocale,
    // i18n
    t,
}) => {
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [isLangOpen, setIsLangOpen] = useState(false);
    const settingsRef = useRef(null);
    const canvasShortRef = useRef(null);
    const canvasLongRef = useRef(null);

    useEffect(() => {
        if (!isSettingsOpen) {
            setIsLangOpen(false);
            return;
        }
        const handleClickOutside = (e) => {
            if (settingsRef.current && !settingsRef.current.contains(e.target)) {
                setIsSettingsOpen(false);
                setIsLangOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isSettingsOpen]);

    const strapShadow = 'drop-shadow(0px 2px 4px rgba(26,17,11,0.15)) drop-shadow(0px 6px 12px rgba(26,17,11,0.08))';

    const traceShortPath = useCallback((ctx) => {
        const sw = strokeWidth / 2;
        ctx.beginPath();
        ctx.moveTo(bucOff + sw, sw);
        ctx.lineTo(bucOff + vBuckleWidth - sw, sw);
        ctx.lineTo(lugOff + vWidth - sw, vShort - sw);
        ctx.lineTo(lugOff + sw, vShort - sw);
        ctx.closePath();
    }, [bucOff, lugOff, vBuckleWidth, vWidth, vShort, strokeWidth]);

    const traceLongPath = useCallback((ctx) => {
        const sw = strokeWidth / 2;
        ctx.beginPath();
        ctx.moveTo(lugOff + sw, sw);
        ctx.lineTo(lugOff + vWidth - sw, sw);
        ctx.lineTo(bucOff + vBuckleWidth - sw, vLong - tipH);
        ctx.quadraticCurveTo(bucOff + vBuckleWidth - sw, vLong - tipH * 0.3, fullW / 2, vLong - 1.5);
        ctx.quadraticCurveTo(bucOff + sw, vLong - tipH * 0.3, bucOff + sw, vLong - tipH);
        ctx.closePath();
    }, [bucOff, lugOff, fullW, vBuckleWidth, vWidth, vLong, tipH, strokeWidth]);

    const drawShortCanvas = useCallback(() => {
        const canvas = canvasShortRef.current;
        if (!canvas || fullW <= 0 || vShort <= 0) return;
        const dpr = window.devicePixelRatio || 1;
        canvas.width = fullW * dpr;
        canvas.height = vShort * dpr;
        canvas.style.width = `${fullW}px`;
        canvas.style.height = `${vShort}px`;
        const ctx = canvas.getContext('2d');
        ctx.scale(dpr, dpr);
        ctx.clearRect(0, 0, fullW, vShort);

        // Layer 1: outer dark border
        traceShortPath(ctx);
        ctx.strokeStyle = '#0A0604';
        ctx.lineWidth = strokeWidth * 0.8;
        ctx.lineJoin = 'round';
        ctx.stroke();

        // Layer 3: center highlight
        ctx.save();
        traceShortPath(ctx);
        ctx.clip();
        const hl = ctx.createLinearGradient(0, 0, fullW, 0);
        hl.addColorStop(0,    'rgba(255,252,248,0)');
        hl.addColorStop(0.36, 'rgba(255,252,248,0.03)');
        hl.addColorStop(0.5,  'rgba(255,252,248,0.13)');
        hl.addColorStop(0.64, 'rgba(255,252,248,0.03)');
        hl.addColorStop(1,    'rgba(255,252,248,0)');
        ctx.fillStyle = hl;
        ctx.fillRect(0, 0, fullW, vShort);
        ctx.restore();

        // Layer 4: edge bevel (burnished highlight)
        traceShortPath(ctx);
        ctx.strokeStyle = 'rgba(255,235,170,0.12)';
        ctx.lineWidth = strokeWidth * 0.3;
        ctx.lineJoin = 'round';
        ctx.stroke();

    }, [fullW, vShort, strokeWidth, traceShortPath]);

    const drawLongCanvas = useCallback(() => {
        const canvas = canvasLongRef.current;
        if (!canvas || fullW <= 0 || vLong <= 0) return;
        const dpr = window.devicePixelRatio || 1;
        canvas.width = fullW * dpr;
        canvas.height = vLong * dpr;
        canvas.style.width = `${fullW}px`;
        canvas.style.height = `${vLong}px`;
        const ctx = canvas.getContext('2d');
        ctx.scale(dpr, dpr);
        ctx.clearRect(0, 0, fullW, vLong);

        // Layer 1: outer dark border
        traceLongPath(ctx);
        ctx.strokeStyle = '#0A0604';
        ctx.lineWidth = strokeWidth * 0.8;
        ctx.lineJoin = 'round';
        ctx.stroke();

        // Layer 3: center highlight
        ctx.save();
        traceLongPath(ctx);
        ctx.clip();
        const hl = ctx.createLinearGradient(0, 0, fullW, 0);
        hl.addColorStop(0,    'rgba(255,252,248,0)');
        hl.addColorStop(0.36, 'rgba(255,252,248,0.03)');
        hl.addColorStop(0.5,  'rgba(255,252,248,0.13)');
        hl.addColorStop(0.64, 'rgba(255,252,248,0.03)');
        hl.addColorStop(1,    'rgba(255,252,248,0)');
        ctx.fillStyle = hl;
        ctx.fillRect(0, 0, fullW, vLong);
        ctx.restore();

        // Layer 4: edge bevel
        traceLongPath(ctx);
        ctx.strokeStyle = 'rgba(255,235,170,0.12)';
        ctx.lineWidth = strokeWidth * 0.3;
        ctx.lineJoin = 'round';
        ctx.stroke();

        // Layer 8: holes with depth (inside clip)
        ctx.save();
        traceLongPath(ctx);
        ctx.clip();
        for (let i = 0; i < 7; i++) {
            const hy = vLong - tipH - (4 * scale) - i * (6.5 * scale);
            const hr = 0.85 * scale;
            // outer shadow halo
            ctx.beginPath();
            ctx.arc(fullW / 2, hy, hr * 1.7, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(0,0,0,0.22)';
            ctx.fill();
            // deep hole (radial gradient)
            const holeGrad = ctx.createRadialGradient(fullW / 2 - hr * 0.3, hy - hr * 0.3, 0, fullW / 2, hy, hr);
            holeGrad.addColorStop(0,   '#060300');
            holeGrad.addColorStop(0.55,'#1A110B');
            holeGrad.addColorStop(1,   'rgba(40,25,15,0.55)');
            ctx.beginPath();
            ctx.arc(fullW / 2, hy, hr, 0, Math.PI * 2);
            ctx.fillStyle = holeGrad;
            ctx.fill();
            // specular glint
            ctx.beginPath();
            ctx.arc(fullW / 2 - hr * 0.25, hy - hr * 0.28, hr * 0.28, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(255,255,255,0.07)';
            ctx.fill();
        }
        ctx.restore();
    }, [fullW, vLong, strokeWidth, scale, tipH, traceLongPath]);

    useEffect(() => { drawShortCanvas(); }, [drawShortCanvas]);
    useEffect(() => { drawLongCanvas(); }, [drawLongCanvas]);

    const handleOpenCart = () => {
        window.parent.postMessage({ type: "OPEN_CART" }, "*");
        setIsSettingsOpen(false);
    };

    const handleGoHome = () => {
        window.parent.postMessage({ type: "GO_HOME" }, "*");
        setIsSettingsOpen(false);
    };

    const handleSelectLocale = (locale) => {
        setCurrentLocale(locale);
        i18n.changeLanguage(locale.code);
        setIsSettingsOpen(false);
    };

    return (
    <>
        {/* HEADER */}
        <div className="w-100 d-flex align-items-center justify-content-center position-relative flex-shrink-0" style={{ height: '48px', marginBottom: '16px' }}>
            {!isOrderFinished && (
                <button
                    id="tour-step-1"
                    onClick={() => setIsConfigOpen(true)}
                    className="shadow-sm position-absolute d-flex justify-content-center align-items-center"
                    style={{ left: '24px', top: '50%', transform: 'translateY(-50%)', zIndex: 50, width: '42px', height: '42px', borderRadius: '50%', backgroundColor: '#C5A075', color: '#fff', border: 'none', opacity: isConfigOpen ? 0 : 1, transition: 'opacity 0.3s' }}
                >
                    <SlidersHorizontal size={20} />
                </button>
            )}
            <div className="fw-bold text-muted text-uppercase" style={{ letterSpacing: '6px', fontSize: '16px' }}>
                {t('luxury_preview', 'LUXURY PREVIEW')}
            </div>
            <button
                onClick={onStartTour}
                className="shadow-sm position-absolute d-flex justify-content-center align-items-center"
                style={{ right: '24px', top: '50%', transform: 'translateY(-50%)', zIndex: 50, width: '42px', height: '42px', borderRadius: '50%', backgroundColor: '#fff', color: '#8B6914', border: '1px dashed #C5A075' }}
            >
                <HelpCircle size={20} />
            </button>
        </div>

        {/* CANVAS AREA */}
        <div className="mobile-canvas-wrapper d-flex justify-content-center flex-grow-1 position-relative w-100" style={{ minHeight: 0 }}>
            <div
                id="tour-step-mobile-select"
                className="d-md-none position-absolute shadow-sm d-flex justify-content-center align-items-center"
                onClick={() => setActiveMobileView('picker')}
                style={{ right: 0, top: '45%', transform: 'translateY(-50%)', zIndex: 100, backgroundColor: '#C5A075', padding: '16px 6px', borderRadius: '8px 0 0 8px', cursor: 'pointer' }}
            >
                <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', color: '#fff', fontWeight: '600', fontSize: '0.85rem', letterSpacing: '1px' }}>Select Leather</span>
            </div>

            <div className="preview-frame-mobile" style={{ width: `${boxWidth * previewScale}px`, height: `${boxHeight * previewScale}px`, position: 'relative' }}>
                <div style={{ transform: `scale(${previewScale})`, transformOrigin: 'top left', width: `${boxWidth}px`, height: `${boxHeight}px`, position: 'absolute', top: 0, left: 0 }}>
                    <div ref={previewContainerRef} className="position-relative" style={{ width: '100%', height: '100%', backgroundColor: 'transparent' }}>

                        {/* DÂY NGẮN */}
                        <div style={{ position: 'absolute', top: `${previewShortTop}px`, left: `${previewShortLeft}px`, width: `${fullW}px`, height: `${vShort}px`, filter: strapShadow }}>
                            {/* Texture layer */}
                            <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', clipPath: previewClipPathShort, backgroundColor: '#E9E4DB', overflow: 'hidden' }}>
                                {imageUrl && <img src={imageUrl} loading="lazy" decoding="async" alt="Texture"
                                    style={{
                                        position: 'absolute',
                                        width: `${trueWidth}px`, height: `${imgDisplayHeight}px`,
                                        left: `-${shortPos.x}px`, top: `-${shortPos.y * yScale}px`,
                                        maxWidth: 'none',
                                        transform: strapRotation !== 0 ? `rotate(${-strapRotation}deg)` : undefined,
                                        transformOrigin: `${shortPos.x + vWidth / 2}px ${shortPos.y * yScale + vShort / 2}px`
                                    }} crossOrigin="anonymous" />}
                            </div>
                            {/* Canvas 2.5D overlay */}
                            <canvas ref={canvasShortRef} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }} />
                        </div>

                        {/* DÂY DÀI */}
                        <div style={{ position: 'absolute', top: `${previewLongTop}px`, left: `${previewLongLeft}px`, width: `${fullW}px`, height: `${vLong}px`, filter: strapShadow }}>
                            {/* Texture layer */}
                            <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', clipPath: previewClipPathLong, backgroundColor: '#E9E4DB', overflow: 'hidden' }}>
                                {imageUrl && <img src={imageUrl} loading="lazy" decoding="async" alt="Texture"
                                    style={{
                                        position: 'absolute',
                                        width: `${trueWidth}px`, height: `${imgDisplayHeight}px`,
                                        left: `-${longPos.x}px`, top: `-${longPos.y * yScale}px`,
                                        maxWidth: 'none',
                                        transform: strapRotation !== 0 ? `rotate(${-strapRotation}deg)` : undefined,
                                        transformOrigin: `${longPos.x + vWidth / 2}px ${longPos.y * yScale + vLong / 2}px`
                                    }} crossOrigin="anonymous" />}
                            </div>
                            {/* Canvas 2.5D overlay */}
                            <canvas ref={canvasLongRef} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }} />
                        </div>

                    </div>
                </div>
            </div>

            <div
                className="d-md-none position-absolute shadow-sm d-flex justify-content-center align-items-center"
                onClick={() => setActiveMobileView('picker')}
                style={{ right: 0, top: '45%', transform: 'translateY(-50%)', zIndex: 100, backgroundColor: '#C5A075', padding: '16px 6px', borderRadius: '8px 0 0 8px', cursor: 'pointer' }}
            >
                <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', color: '#fff', fontWeight: '600', fontSize: '0.85rem', letterSpacing: '1px' }}>Select Leather</span>
            </div>
        </div>

        {/* SETTINGS BUTTON */}
        <div ref={settingsRef} className="flex-shrink-0 position-relative" style={{ padding: '8px 24px 12px 24px', alignSelf: 'flex-start' }}>
            <button
                onClick={() => setIsSettingsOpen(prev => !prev)}
                className="shadow-sm d-flex justify-content-center align-items-center"
                style={{
                    width: '42px', height: '42px', borderRadius: '50%',
                    backgroundColor: isSettingsOpen ? 'var(--color-gold)' : 'var(--color-bg-card)',
                    color: isSettingsOpen ? '#fff' : 'var(--color-gold)',
                    border: '1px solid var(--color-gold)',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s, color 0.2s',
                }}
            >
                <Settings size={18} />
            </button>

            {isSettingsOpen && (
                <div
                    className="position-absolute shadow"
                    style={{
                        bottom: 'calc(100% + 8px)',
                        left: 0,
                        marginLeft: '8px',
                        backgroundColor: 'var(--color-bg-card)',
                        border: '1px solid var(--color-border)',
                        borderRadius: '12px',
                        padding: '6px',
                        minWidth: '180px',
                        zIndex: 200,
                    }}
                >
                    {localesList && localesList.length > 1 && (
                        <div style={{ position: 'relative' }}>
                            <button
                                onClick={() => setIsLangOpen(prev => !prev)}
                                className="d-flex align-items-center justify-content-between w-100"
                                style={{
                                    padding: '10px 12px', border: 'none', borderRadius: '8px',
                                    backgroundColor: isLangOpen ? 'var(--color-gold-muted)' : 'transparent',
                                    color: 'var(--color-text)', cursor: 'pointer',
                                    fontSize: '0.875rem', fontWeight: '600',
                                    transition: 'background-color 0.15s',
                                }}
                                onMouseEnter={e => { if (!isLangOpen) e.currentTarget.style.backgroundColor = 'var(--color-gold-muted)'; }}
                                onMouseLeave={e => { if (!isLangOpen) e.currentTarget.style.backgroundColor = 'transparent'; }}
                            >
                                <div className="d-flex align-items-center gap-2">
                                    <Globe size={16} color="var(--color-gold)" />
                                    {t('language', 'Language')}
                                </div>
                                <ChevronRight size={13} color="var(--color-text-muted)"
                                    style={{ transform: isLangOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                            </button>

                            {isLangOpen && (
                                <div style={{
                                    position: 'absolute',
                                    left: 'calc(100% + 8px)',
                                    top: 0,
                                    backgroundColor: 'var(--color-bg-card)',
                                    border: '1px solid var(--color-border)',
                                    borderRadius: '12px',
                                    padding: '6px',
                                    minWidth: '110px',
                                    zIndex: 300,
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                                }}>
                                    {localesList.map(locale => (
                                        <button
                                            key={locale._id || locale.code}
                                            onClick={() => handleSelectLocale(locale)}
                                            className="d-flex align-items-center gap-2 w-100"
                                            style={{
                                                padding: '8px 12px', border: 'none', borderRadius: '8px',
                                                backgroundColor: currentLocale?.code === locale.code ? 'var(--color-gold-muted)' : 'transparent',
                                                cursor: 'pointer', fontSize: '13px', fontWeight: '700',
                                                color: currentLocale?.code === locale.code ? 'var(--color-gold)' : 'var(--color-text)',
                                                transition: 'all 0.15s', whiteSpace: 'nowrap',
                                            }}
                                            onMouseEnter={e => { if (currentLocale?.code !== locale.code) e.currentTarget.style.backgroundColor = 'var(--color-gold-muted)'; }}
                                            onMouseLeave={e => { if (currentLocale?.code !== locale.code) e.currentTarget.style.backgroundColor = 'transparent'; }}
                                        >
                                            {locale.iconFile && (
                                                <img src={locale.iconFile} alt="" loading="lazy"
                                                    style={{ width: '16px', height: '11px', objectFit: 'cover', borderRadius: '2px', flexShrink: 0 }} />
                                            )}
                                            {locale.displayCode || locale.code}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {localesList && localesList.length > 1 && (
                        <div style={{ height: '1px', backgroundColor: 'var(--color-border)', margin: '4px 8px' }} />
                    )}

                    <button
                        onClick={handleOpenCart}
                        className="d-flex align-items-center gap-2 w-100"
                        style={{ padding: '10px 12px', border: 'none', borderRadius: '8px', backgroundColor: 'transparent', color: 'var(--color-text)', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '600', transition: 'background-color 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--color-gold-muted)'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                        <ShoppingCart size={16} color="var(--color-gold)" />
                        {t('my_cart', 'My Cart')}
                    </button>

                    <button
                        onClick={handleGoHome}
                        className="d-flex align-items-center gap-2 w-100"
                        style={{ padding: '10px 12px', border: 'none', borderRadius: '8px', backgroundColor: 'transparent', color: 'var(--color-text)', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '600', transition: 'background-color 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--color-gold-muted)'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                        <House size={16} color="var(--color-gold)" />
                        {t('go_home', 'Home')}
                    </button>
                </div>
            )}
        </div>
    </>
    );
};

export default PreviewColumn;
